/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/CompensationMasterData/payGradeSchema.ts
 * PURPOSE: EntitySchema<PayGrade> wiring the generic MasterDataCrudPage to the
 *          new /api/v2/compensation/pay-grades CRUD + Excel API (BR-COMP-002 §8).
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-12
 * COMPLIANCE: SDLC 6.3.1 + BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Vietnamese Compensation Master Data (thang bảng lương, Bộ luật Lao động 2019)
 * - Impact: Pay bands gate base_salary assignment (BR-COMP-002 R4 band validation)
 * - Stakeholders: CFO / HR Director (TENANT_ADMIN)
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, Page-level config
 * - Pattern: Config-Driven UI
 * - Dependencies: MasterDataCrudPage, compensationMasterData API service
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/08-compensation/BR-COMP-002-job-family-paygrade-management.md (§8.1)
 * - Backend API: backend/app/api/v2/endpoints/compensation_master_data.py
 * - Route: frontend/src/App.tsx (/tenant-admin/compensation/pay-grades)
 */

import type { EntitySchema, FieldDef } from '@/components/master-data/types';
import type { ColumnDef } from '@/components/dashboard/DataTable';
import {
  PAY_GRADE_API_BASE,
  compensationMasterDataClient,
  type PayGrade,
} from '@/services/api/compensationMasterData';

const formatVnd = (value: unknown): string =>
  value === null || value === undefined || value === ''
    ? '—'
    : `₫${Number(value).toLocaleString('vi-VN')}`;

const validateOptionalJson = (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  if (typeof value === 'object') return undefined;
  try {
    JSON.parse(String(value));
    return undefined;
  } catch {
    return 'JSON không hợp lệ (VD: {"vi": "Bậc 5", "en": "Level 5"})';
  }
};

const validateNonNegative = (label: string) => (value: unknown): string | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  return Number(value) >= 0 ? undefined : `${label} phải >= 0`;
};

const columns: ColumnDef<PayGrade>[] = [
  { key: 'level', label: 'Bậc', sortable: true, align: 'center', width: '80px' },
  { key: 'name', label: 'Tên bậc', sortable: true },
  { key: 'tier', label: 'Nhóm', render: (value) => (value as string) || '—' },
  { key: 'min_salary', label: 'Lương tối thiểu', align: 'right', render: formatVnd },
  { key: 'max_salary', label: 'Lương tối đa', align: 'right', render: formatVnd },
  { key: 'step_amount', label: 'Mức tăng/step', align: 'right', render: formatVnd },
  { key: 'currency', label: 'Tiền tệ', align: 'center', width: '90px' },
];

const formFields: FieldDef[] = [
  {
    key: 'level',
    label: 'Bậc (level)',
    type: 'number',
    required: true,
    helpText:
      'Khóa tự nhiên (duy nhất trong tổ chức). Không sửa được sau khi tạo — backend bỏ qua thay đổi.',
  },
  {
    key: 'name',
    label: 'Tên bậc',
    type: 'text',
    required: true,
    placeholder: 'L5',
    helpText:
      'Đây cũng là khóa tra cứu khi gán compensation (_get_pay_grade_by_name) — giữ đúng quy ước hiện có (VD: "L5").',
  },
  {
    key: 'tier',
    label: 'Nhóm bậc',
    type: 'select',
    options: [
      { value: 'Entry', label: 'Entry' },
      { value: 'Junior', label: 'Junior' },
      { value: 'Mid', label: 'Mid' },
      { value: 'Senior', label: 'Senior' },
      { value: 'Manager', label: 'Manager' },
      { value: 'Director', label: 'Director' },
      { value: 'Executive', label: 'Executive' },
    ],
  },
  {
    key: 'min_salary',
    label: 'Lương tối thiểu (VND)',
    type: 'number',
    required: true,
    placeholder: '8000000',
    validate: validateNonNegative('Lương tối thiểu'),
    // Cross-field rule (min_salary <= max_salary) is enforced server-side (422 +
    // Vietnamese message) — FieldDef.validate only sees one value at a time.
  },
  {
    key: 'max_salary',
    label: 'Lương tối đa (VND)',
    type: 'number',
    required: true,
    placeholder: '15000000',
    validate: validateNonNegative('Lương tối đa'),
  },
  {
    key: 'step_amount',
    label: 'Mức tăng mỗi step (VND)',
    type: 'number',
    required: true,
    defaultValue: 0,
    validate: validateNonNegative('Mức tăng mỗi step'),
  },
  {
    key: 'currency',
    label: 'Tiền tệ',
    type: 'select',
    defaultValue: 'VND',
    options: [
      { value: 'VND', label: 'VND' },
      { value: 'THB', label: 'THB' },
      { value: 'USD', label: 'USD' },
    ],
  },
  {
    key: 'name_localized',
    label: 'Tên đa ngôn ngữ (JSON)',
    type: 'textarea',
    rows: 3,
    placeholder: '{"vi": "Bậc 5", "en": "Level 5"}',
    validate: validateOptionalJson,
  },
];

/** RBAC: TENANT_ADMIN tier only — see jobFamilySchema.ts (same @cpo resolution). */
export const payGradeSchema: EntitySchema<PayGrade> = {
  entityKey: 'pay-grades',
  apiBasePath: PAY_GRADE_API_BASE,
  entityLabel: 'Bậc lương',
  httpClient: compensationMasterDataClient,
  columns,
  formFields,
  permissions: {
    create: ['TENANT_OWNER', 'TENANT_ADMIN'],
    edit: ['TENANT_OWNER', 'TENANT_ADMIN'],
    delete: ['TENANT_OWNER', 'TENANT_ADMIN'],
  },
  importExport: {
    enabled: true,
    templateColumns: [
      'level',
      'name',
      'name_localized',
      'tier',
      'min_salary',
      'max_salary',
      'step_amount',
      'currency',
    ],
    exportFileName: 'pay-grades.xlsx',
  },
};

export default payGradeSchema;
